import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Globe, Tag, Plus, Upload, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MerchantApi } from "@/api";
import type { MerchantOfferAddressDetailVO } from "@/api/merchant.api";
import { format } from "date-fns";
import {
  appendOfferUrlFormField,
  type OfferPageCategoryOption,
} from "./AddOfferForOfferPageDialog";
import {
  TermsAndConditionsSection,
  parseTermsAndConditionsApiValue,
  serializeTermsAndConditionsForApi,
} from "@/components/merchant/TermsAndConditionsSection";
import {
  MerchantOfferAddressDetailsSection,
  buildMerchantOfferAddressPayload,
  createEmptyMerchantOfferAddressRow,
  voToAddressFormRow,
  type MerchantOfferAddressFormRow,
} from "@/components/merchant/MerchantOfferAddressDetailsSection";

export type OfferForEditInitial = {
  id: string;
  title: string;
  description: string;
  startDateInMillis?: number | string;
  endDateInMillis?: number | string;
  offerURL: string;
  categoryId: string;
  imageSrc: string;
  /** Comma-separated from API */
  termsAndConditions?: string;
  /** When API returns saved offer locations */
  addressDetailsVOList?: MerchantOfferAddressDetailVO[];
};

const optionalOfferUrlSchema = z.union([
  z.literal(""),
  z.string().trim().url({ message: "Please enter a valid URL" }),
]);

const offerEditSchema = z
  .object({
    name: z.string().min(2, { message: "Title is required" }),
    description: z.string().min(2, { message: "Description is required" }),
    startDate: z.string().min(1, { message: "Start date is required" }),
    endDate: z.string().min(1, { message: "End date is required" }),
    offerUrl: optionalOfferUrlSchema,
    categoryId: z.string().min(1, { message: "Select a category" }),
  })
  .superRefine((data, ctx) => {
    const start = new Date(data.startDate);
    const end = new Date(data.endDate);
    if (start > end) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["startDate"],
        message: "Start date cannot be after end date",
      });
    }
  });

type FormValues = z.infer<typeof offerEditSchema>;

function millisToDateInput(ms?: number | string | null): string {
  if (ms == null || ms === "") return "";
  const n = typeof ms === "string" ? Number(ms) : ms;
  if (!Number.isFinite(n)) return "";
  try {
    return format(new Date(n), "yyyy-MM-dd");
  } catch {
    return "";
  }
}

type EditOfferForOfferPageDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchantId: string;
  categories: OfferPageCategoryOption[];
  initialOffer: OfferForEditInitial;
  onSuccess?: (savedOfferUrl: string) => void;
};

export function EditOfferForOfferPageDialog({
  open,
  onOpenChange,
  merchantId,
  categories,
  initialOffer,
  onSuccess,
}: EditOfferForOfferPageDialogProps) {
  const [imageFile, setImageFile] = React.useState<File | null>(null);
  const [imagePreview, setImagePreview] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [termsLines, setTermsLines] = React.useState<string[]>([]);
  const [addressRows, setAddressRows] = React.useState<
    MerchantOfferAddressFormRow[]
  >([createEmptyMerchantOfferAddressRow()]);
  const today = new Date().toISOString().split("T")[0];

  const form = useForm<FormValues>({
    resolver: zodResolver(offerEditSchema),
    defaultValues: {
      name: "",
      description: "",
      startDate: "",
      endDate: "",
      offerUrl: "",
      categoryId: "",
    },
  });

  React.useEffect(() => {
    if (!open) {
      form.reset();
      setImageFile(null);
      setImagePreview(null);
      setTermsLines([]);
      setAddressRows([createEmptyMerchantOfferAddressRow()]);
      return;
    }
    form.reset({
      name: initialOffer.title || "",
      description: initialOffer.description || "",
      startDate: millisToDateInput(initialOffer.startDateInMillis),
      endDate: millisToDateInput(initialOffer.endDateInMillis),
      offerUrl: initialOffer.offerURL ?? "",
      categoryId: initialOffer.categoryId || "",
    });
    setImageFile(null);
    setImagePreview(initialOffer.imageSrc || null);
    setTermsLines(
      parseTermsAndConditionsApiValue(initialOffer.termsAndConditions)
    );
    setAddressRows(
      initialOffer.addressDetailsVOList?.length
        ? initialOffer.addressDetailsVOList.map(voToAddressFormRow)
        : [createEmptyMerchantOfferAddressRow()]
    );
    // Only re-sync when the dialog opens or a different offer is edited (see key on parent).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialOffer.id]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (data: FormValues) => {
    const addressPayload = buildMerchantOfferAddressPayload(addressRows);
    if (addressPayload && "error" in addressPayload) {
      toast.error(addressPayload.error);
      return;
    }

    const offersOfMerchantId = Number(initialOffer.id);
    if (
      addressPayload &&
      "addressDetailsVOList" in addressPayload &&
      addressPayload.addressDetailsVOList.length > 0 &&
      (!Number.isFinite(offersOfMerchantId) || offersOfMerchantId <= 0)
    ) {
      toast.error("Cannot save addresses: this offer has no valid id.");
      return;
    }

    setSubmitting(true);
    const offerUrl = (data.offerUrl ?? "").trim();
    try {
      const formData = new FormData();
      if (imageFile) {
        formData.append("offerImage", imageFile);
      }
      formData.append("id", String(initialOffer.id));
      formData.append("merchantId", merchantId);
      formData.append("title", data.name);
      appendOfferUrlFormField(formData, data.offerUrl);
      formData.append("description", data.description);
      formData.append(
        "startDateInMillis",
        String(new Date(data.startDate).getTime())
      );
      formData.append(
        "endDateInMillis",
        String(new Date(data.endDate).getTime())
      );
      formData.append("categoryId", data.categoryId);
      formData.append(
        "termsAndConditions",
        serializeTermsAndConditionsForApi(termsLines)
      );

      await MerchantApi.createOrUpdateMerchantOfferForOfferPage(formData);

      if (
        addressPayload &&
        "addressDetailsVOList" in addressPayload &&
        addressPayload.addressDetailsVOList.length > 0
      ) {
        try {
          await MerchantApi.createOrUpdateMerchantOfferAddressDetails({
            offersOfMerchantId: offersOfMerchantId,
            addressDetailsVOList: addressPayload.addressDetailsVOList,
          });
        } catch (addrErr: unknown) {
          const msg =
            addrErr &&
            typeof addrErr === "object" &&
            "message" in addrErr &&
            typeof (addrErr as { message?: unknown }).message === "string"
              ? (addrErr as { message: string }).message
              : "Offer was updated, but saving addresses failed.";
          toast.error(msg);
        }
      }

      toast.success("Offer updated successfully");
      onOpenChange(false);
      onSuccess?.(offerUrl);
    } catch (error: unknown) {
      const message =
        error && typeof error === "object" && "message" in error
          ? String((error as { message?: string }).message)
          : "Failed to update offer";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const categoryLabel = (c: OfferPageCategoryOption) => {
    const name = c.categoryName || c.categoryCode || "Category";
    if (c.categoryCode && c.categoryName !== c.categoryCode) {
      return `${c.categoryName} (${c.categoryCode})`;
    }
    return name;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Edit Offer
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <div className="space-y-2">
              <FormLabel>Image</FormLabel>
              <p className="text-xs text-muted-foreground">
                Leave unchanged to keep the current image, or upload a new one.
              </p>
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="edit-offer-for-offer-page-image"
                />
                <label
                  htmlFor="edit-offer-for-offer-page-image"
                  className="cursor-pointer block"
                >
                  <div className="w-full h-48 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:border-gray-400 transition-colors">
                    {imagePreview ? (
                      <div className="relative w-full h-full">
                        <img
                          src={imagePreview}
                          alt="Offer preview"
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center">
                          <Upload className="h-8 w-8 text-white opacity-0 hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ) : (
                      <>
                        <Plus className="h-12 w-12 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500">
                          Click to upload offer image
                        </p>
                      </>
                    )}
                  </div>
                </label>
              </div>
            </div>

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title*</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Summer Discount" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description*</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Offer description" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="offerUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Offer URL</FormLabel>
                  <FormControl>
                    <div className="flex gap-2">
                      <Input
                        placeholder="https://example.com/offer"
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="shrink-0"
                        onClick={() => {
                          navigator.clipboard
                            .readText()
                            .then((text) => {
                              if (text.startsWith("http")) {
                                field.onChange(text);
                                toast.info("URL pasted");
                              }
                            })
                            .catch(() => {
                              toast.error("Could not read from clipboard");
                            });
                        }}
                      >
                        <Globe className="h-4 w-4" />
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date*</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date*</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        min={form.watch("startDate") || today}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="categoryId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category*</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categories.length === 0 ? (
                        <div className="px-4 py-2 text-gray-500 text-sm">
                          No categories on this merchant.
                        </div>
                      ) : (
                        categories.map((category) => (
                          <SelectItem
                            key={category.id}
                            value={String(category.id)}
                          >
                            {categoryLabel(category)}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <MerchantOfferAddressDetailsSection
              rows={addressRows}
              onChange={setAddressRows}
            />

            <TermsAndConditionsSection lines={termsLines} onChange={setTermsLines} />

            <DialogFooter className="sm:justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-rose-600 hover:bg-rose-700"
                disabled={submitting || categories.length === 0}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Update Offer"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
